import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  Alert,
  Spinner,
  ButtonGroup,
  InputGroup,
  ProgressBar
} from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import Layout from '../components/layouts/Main';
import config from '../config';
import { useHasPermission, PERMISSIONS } from '../utils/permissions';
import PermissionGate from '../components/common/PermissionGate';
import {
  FiPlus,
  FiSearch,
  FiFilter,
  FiRefreshCw,
  FiEye,
  FiEdit,
  FiClock,
  FiCheckCircle,
  FiUsers,
  FiPackage,
  FiTrendingUp,
  FiAlertCircle
} from 'react-icons/fi';

const RFQManagement = () => {
  const navigate = useNavigate();

  // Permission checks
  const canViewRFQs = useHasPermission(PERMISSIONS.QUOTATION_VIEW);
  // const canCreateRFQs = useHasPermission(PERMISSIONS.QUOTATION_CREATE);
  // const canEditRFQs = useHasPermission(PERMISSIONS.QUOTATION_EDIT);
  // const canDeleteRFQs = useHasPermission(PERMISSIONS.QUOTATION_DELETE);

  // State management
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    rfq_type: '',
    status: '',
    priority: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    current_page: 1,
    total_pages: 1,
    total_count: 0,
    per_page: 20
  });
  const [summary, setSummary] = useState({});
  const [activeTab, setActiveTab] = useState('all');


  const [showConsolidationModal, setShowConsolidationModal] = useState(false);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [consolidationCandidates, setConsolidationCandidates] = useState([]);
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  // const [selectedCandidates, setSelectedCandidates] = useState([]);

  const fetchRFQs = useCallback(async () => {
    if (!canViewRFQs) {
      toast.error("You don't have permission to view RFQs");
      return;
    }

    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.current_page,
        limit: pagination.per_page,
        ...filters
      });

      // Add tab-specific filters
      if (activeTab !== 'all') {
        if (activeTab === 'consolidated') {
          params.set('rfq_type', 'consolidated');
        } else if (activeTab === 'individual') {
          params.set('rfq_type', 'individual');
        } else if (activeTab === 'pending') {
          params.set('status', 'pending_responses');
        } else if (activeTab === 'completed') {
          params.set('status', 'completed');
        }
      }

      const response = await axios.get(`${config.API_BASE_URL}/rfqs?${params}`);

      setRfqs(response.data.rfqs || []);
      setPagination(response.data.pagination || {});
      setSummary(response.data.summary || {});

    } catch (error) {
      console.error('Error fetching RFQs:', error);
      toast.error('Failed to fetch RFQs');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.current_page, activeTab, pagination.per_page, canViewRFQs]);

  useEffect(() => {
    if (canViewRFQs) {
      fetchRFQs();
    }
  }, [filters, pagination.current_page, activeTab, canViewRFQs, fetchRFQs]);

  const fetchConsolidationCandidates = async () => {
    try {
      setLoadingCandidates(true);
      const response = await axios.get(`${config.API_BASE_URL}/rfqs/consolidation/candidates?consolidation_type=product`);
      setConsolidationCandidates(response.data.consolidationOpportunities || []);
    } catch (error) {
      console.error('Error fetching consolidation candidates:', error);
      toast.error('Failed to fetch consolidation opportunities');
    } finally {
      setLoadingCandidates(false);
    }
  };

  const handleCreateConsolidatedRFQ = async (candidateGroup) => {
    try {
      const consolidationData = {
        purchase_request_ids: candidateGroup.purchaseRequests,
        consolidation_type: 'product',
        rfq_title: `Consolidated RFQ for ${candidateGroup.product?.productName}`,
        delivery_location: candidateGroup.deliveryLocations[0] || '',
        delivery_date: candidateGroup.deliveryDates[0] || new Date(),
        raised_by: 'current-user-id', // Replace with actual user ID
        note: `Consolidated from ${candidateGroup.purchaseRequests.length} purchase requests for bulk pricing`,
        vendor_ids: [], // Add vendor selection logic
        broker_ids: [], // Add broker selection logic
        response_deadline: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days from now
      };

      const response = await axios.post(`${config.API_BASE_URL}/rfqs/consolidated`, consolidationData);
      
      if (response.data.success) {
        toast.success('Consolidated RFQ created successfully!');
        setShowConsolidationModal(false);
        fetchRFQs();
        fetchConsolidationCandidates(); // Refresh candidates
      }
    } catch (error) {
      console.error('Error creating consolidated RFQ:', error);
      toast.error('Failed to create consolidated RFQ');
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
    setPagination(prev => ({ ...prev, current_page: 1 }));
  };

  const clearFilters = () => {
    setFilters({
      rfq_type: '',
      status: '',
      priority: '',
      date_from: '',
      date_to: '',
      search: ''
    });
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'draft': { bg: 'secondary', text: 'Draft' },
      'sent': { bg: 'primary', text: 'Sent' },
      'responses_received': { bg: 'info', text: 'Responses Received' },
      'completed': { bg: 'success', text: 'Completed' },
      'cancelled': { bg: 'danger', text: 'Cancelled' }
    };
    const statusInfo = statusMap[status] || { bg: 'secondary', text: status };
    return <Badge bg={statusInfo.bg}>{statusInfo.text}</Badge>;
  };

  const getRFQTypeBadge = (type) => {
    return (
      <Badge bg={type === 'consolidated' ? 'warning' : 'info'} className="fs-11 small">
        {type === 'consolidated' ? 'Consolidated' : 'Individual'}
      </Badge>
    );
  };

  const renderRFQTable = () => (
    <>
    <div className="table-responsive">
      <Table hover>
        <thead className="bg-light">
          <tr>
            <th>RFQ #</th>
            {/* <th>Type</th> */}
            <th>Purchase Requests</th>
            <th>Items</th>
            <th>Recipients</th>
            <th>Responses</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {rfqs.length === 0 ? (
            <tr>
              <td colSpan="9" className="text-center py-4">
                <div className="text-muted">
                  {loading ? 'Loading RFQs...' : 'No RFQs found matching your criteria'}
                </div>
              </td>
            </tr>
          ) : (
            rfqs.map((rfq) => (
              <tr key={rfq._id}>
                <td>
                  <strong className="text-primary">{rfq.rfq_id}</strong>
                  <div>{getRFQTypeBadge(rfq.rfq_type)}</div>
                  {rfq.is_overdue && (
                    <div><Badge bg="danger" size="sm">Overdue</Badge></div>
                  )}

                </td>
                {/* <td>{getRFQTypeBadge(rfq.rfq_type)}</td> */}
                <td>
                  <div className="d-flex flex-column">
                    <span>
                      <strong>{rfq.purchase_requests?.length || (rfq.purchase_request ? 1 : 0)}</strong> PRs
                    </span>
                    {rfq.rfq_type === 'consolidated' && (
                      <small className="text-success">
                        <FiTrendingUp className="me-1" />
                        Consolidated
                      </small>
                    )}
                  </div>
                </td>
                <td>
                  <div>
                    <FiPackage className="me-1" />
                    <strong>{rfq.total_items || 0}</strong> items
                    <div>
                      <small className="text-muted">
                        {rfq.unique_products || 0} unique products
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <div>
                    <FiUsers className="me-1" />
                    <strong>{rfq.recipient_count || 0}</strong> recipients
                  </div>
                </td>
                <td>
                  <div>
                    <strong>{rfq.response_count || 0}</strong> responses
                    <div>
                      <ProgressBar 
                        now={rfq.response_rate || 0} 
                        size="sm" 
                        style={{ height: '4px' }}
                        variant={
                          (rfq.response_rate || 0) > 80 ? 'success' : 
                          (rfq.response_rate || 0) > 50 ? 'warning' : 'danger'
                        }
                      />
                      <small className="text-muted">{rfq.response_rate || 0}% rate</small>
                    </div>
                  </div>
                </td>
                <td>{getStatusBadge(rfq.status)}</td>
                <td>
                  <div>
                    {new Date(rfq.created_at).toLocaleDateString()}
                    <div>
                      <small className="text-muted">
                        {rfq.days_since_created} days ago
                      </small>
                    </div>
                  </div>
                </td>
                <td>
                  <ButtonGroup size="sm">
                    <Button
                      variant="outline-primary"
                      onClick={() => navigate(`/rfqs/${rfq._id}`)}
                      title="View Details"
                    >
                      <FiEye />
                    </Button>
                    {/* {rfq.status === 'draft' && (
                      <PermissionGate
                        require={PERMISSIONS.QUOTATION_EDIT}
                        fallback={
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            disabled
                            title="Edit access restricted"
                          >
                            <FiEdit />
                          </Button>
                        }
                      >
                        <Button
                          variant="outline-warning"
                          onClick={() => navigate(`/rfqs/${rfq._id}/edit`)}
                          title="Edit RFQ"
                        >
                          <FiEdit />
                        </Button>
                      </PermissionGate>
                    )} */}
                  </ButtonGroup>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
    </div>

    {/* Pagination inside renderRFQTable */}
    {pagination.total_pages > 1 && (
      <div className="d-flex justify-content-between align-items-center mt-3 px-3 pb-3">
        <div className="text-muted small">
          Showing {(pagination.current_page - 1) * pagination.per_page + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total_count)} of {pagination.total_count} RFQs
        </div>
        <nav>
          <ul className="pagination mb-0">
            <li className={`page-item ${pagination.current_page === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page - 1 }))}
              >
                Previous
              </button>
            </li>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map(page => (
              <li key={page} className={`page-item ${pagination.current_page === page ? 'active' : ''}`}>
                <button
                  className="page-link"
                  onClick={() => setPagination(prev => ({ ...prev, current_page: page }))}
                >
                  {page}
                </button>
              </li>
            ))}
            <li className={`page-item ${pagination.current_page === pagination.total_pages ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setPagination(prev => ({ ...prev, current_page: prev.current_page + 1 }))}
              >
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    )}
    </>
  );

  // const handleSendReminders = async (rfqId) => {
  //   try {
  //     const response = await axios.post(`${config.API_BASE_URL}/rfqs/${rfqId}/send-reminders`);
  //     toast.success(response.data.message);
  //     fetchRFQs(); // Refresh data
  //   } catch (error) {
  //     console.error('Error sending reminders:', error);
  //     toast.error('Failed to send reminders');
  //   }
  // };

  const renderConsolidationModal = () => (
    <Modal show={showConsolidationModal} onHide={() => setShowConsolidationModal(false)} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>
          <FiTrendingUp className="me-2" />
          Consolidation Opportunities
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loadingCandidates ? (
          <div className="text-center py-4">
            <Spinner animation="border" />
            <p className="mt-2">Analyzing consolidation opportunities...</p>
          </div>
        ) : (
          <>
            {consolidationCandidates.length === 0 ? (
              <Alert variant="info">
                <FiAlertCircle className="me-2" />
                No consolidation opportunities found at this time.
              </Alert>
            ) : (
              <div>
                <Alert variant="success">
                  <strong>{consolidationCandidates.length}</strong> consolidation opportunities found!
                  These can potentially save administrative time and reduce costs through bulk pricing.
                </Alert>
                
                {consolidationCandidates.map((candidate, index) => (
                  <Card key={index} className="mb-3">
                    <Card.Header>
                      <div className="d-flex justify-content-between align-items-center">
                        <div>
                          <h6 className="mb-0">
                            {candidate.product?.productName || `Group ${index + 1}`}
                          </h6>
                          <small className="text-muted">
                            {candidate.purchaseRequests.length} Purchase Requests • 
                            Total Quantity: {candidate.totalQuantity} • 
                            Estimated Value: ₹{candidate.totalValue?.toLocaleString()}
                          </small>
                        </div>
                        <Button
                          variant="success"
                          size="sm"
                          onClick={() => handleCreateConsolidatedRFQ(candidate)}
                        >
                          Create Consolidated RFQ
                        </Button>
                      </div>
                    </Card.Header>
                    <Card.Body>
                      <Row>
                        <Col md={8}>
                          <h6>Purchase Requests to Consolidate:</h6>
                          <div className="mb-3">
                            {candidate.items.map((item, idx) => (
                              <Badge key={idx} bg="light" text="dark" className="me-2 mb-1">
                                {item.purchaseRequestNo}: {item.quantity} {item.unit}
                              </Badge>
                            ))}
                          </div>
                          
                          <h6>Benefits:</h6>
                          <ul className="list-unstyled">
                            <li><FiCheckCircle className="text-success me-2" />Bulk pricing negotiations</li>
                            <li><FiCheckCircle className="text-success me-2" />Reduced administrative overhead</li>
                            <li><FiCheckCircle className="text-success me-2" />Better vendor relationships</li>
                            <li><FiCheckCircle className="text-success me-2" />Streamlined delivery coordination</li>
                          </ul>
                        </Col>
                        <Col md={4}>
                          <div className="bg-light p-3 rounded">
                            <h6>Consolidation Impact</h6>
                            <div className="mb-2">
                              <strong>Priority:</strong> 
                              <Badge bg="info" className="ms-2">
                                {candidate.priorities.join(', ')}
                              </Badge>
                            </div>
                            <div className="mb-2">
                              <strong>Delivery Locations:</strong>
                              <div>{candidate.deliveryLocations.join(', ') || 'Not specified'}</div>
                            </div>
                            <div>
                              <strong>Potential Savings:</strong>
                              <div className="text-success">
                                {candidate.consolidationBenefit === 'bulk_pricing' ? 
                                  'Up to 15% cost savings' : 
                                  'Administrative efficiency'
                                }
                              </div>
                            </div>
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => setShowConsolidationModal(false)}>
          Close
        </Button>
        <Button variant="primary" onClick={fetchConsolidationCandidates}>
          <FiRefreshCw className="me-2" />
          Refresh Analysis
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderFiltersModal = () => (
    <Modal show={showFiltersModal} onHide={() => setShowFiltersModal(false)}>
      <Modal.Header closeButton>
        <Modal.Title>
          <FiFilter className="me-2" />
          Advanced Filters
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>RFQ Type</Form.Label>
              <Form.Select
                value={filters.rfq_type}
                onChange={(e) => handleFilterChange('rfq_type', e.target.value)}
              >
                <option value="">All Types</option>
                <option value="individual">Individual</option>
                <option value="consolidated">Consolidated</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Status</Form.Label>
              <Form.Select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="draft">Draft</option>
                <option value="sent">Sent</option>
                <option value="responses_received">Responses Received</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </Form.Select>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date From</Form.Label>
              <Form.Control
                type="date"
                value={filters.date_from}
                onChange={(e) => handleFilterChange('date_from', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label>Date To</Form.Label>
              <Form.Control
                type="date"
                value={filters.date_to}
                onChange={(e) => handleFilterChange('date_to', e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group className="mb-3">
              <Form.Label>Search</Form.Label>
              <Form.Control
                type="text"
                placeholder="Search RFQ ID, notes, or tags..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={clearFilters}>
          Clear All
        </Button>
        <Button variant="secondary" onClick={() => setShowFiltersModal(false)}>
          Close
        </Button>
        <Button variant="primary" onClick={() => setShowFiltersModal(false)}>
          Apply Filters
        </Button>
      </Modal.Footer>
    </Modal>
  );

  const renderSummaryCards = () => (
    <Row className="mb-4 d-none">
      <Col md={3}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-primary mb-2">
              <FiPackage size={24} />
            </div>
            <h4 className="text-primary">{summary.total_active || 0}</h4>
            <p className="text-muted mb-0">Total RFQs</p>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-warning mb-2">
              <FiTrendingUp size={24} />
            </div>
            <h4 className="text-warning">{summary.consolidated_rfqs || 0}</h4>
            <p className="text-muted mb-0">Consolidated RFQs</p>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-info mb-2">
              <FiClock size={24} />
            </div>
            <h4 className="text-info">{summary.pending_responses || 0}</h4>
            <p className="text-muted mb-0">Pending Responses</p>
          </Card.Body>
        </Card>
      </Col>
      <Col md={3}>
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center">
            <div className="text-danger mb-2">
              <FiAlertCircle size={24} />
            </div>
            <h4 className="text-danger">{summary.overdue_rfqs || 0}</h4>
            <p className="text-muted mb-0">Overdue RFQs</p>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  // If user doesn't have view permission, show access denied
  if (!canViewRFQs) {
    return (
      <Layout>
        <Container fluid className="py-4">
          <Alert variant="danger" className="d-flex align-items-center">
            <i className="fas fa-exclamation-triangle me-2"></i>
            <div>
              <strong>Access Denied</strong>
              <p className="mb-0">You don't have permission to view RFQ management. Contact your administrator for access.</p>
            </div>
          </Alert>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container fluid className="py-4">
        {/* Header */}
        <Row className="mb-4">
          <Col>
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h2 className="mb-1">RFQ Management</h2>
                <p className="text-muted mb-0">
                  Manage Request for Quotations with consolidation capabilities
                </p>
              </div>
              <div className="d-flex gap-2">
                <Button
                  variant="outline-info"
                  onClick={() => {
                    setShowConsolidationModal(true);
                    fetchConsolidationCandidates();
                  }}
                  className="d-none"
                >
                  <FiTrendingUp className="me-1" />
                  Find Consolidation Opportunities
                </Button>
                <Button
                  variant="outline-secondary"
                  onClick={() => setShowFiltersModal(true)}
                >
                  <FiFilter className="me-1" />
                  Filters
                </Button>
                <Button
                  variant="outline-primary"
                  onClick={fetchRFQs}
                >
                  <FiRefreshCw className="me-1" />
                  Refresh
                </Button>
                <PermissionGate
                  require={PERMISSIONS.QUOTATION_CREATE}
                  fallback={
                    <Button variant="outline-secondary" disabled title="You don't have permission to create RFQs">
                      <FiPlus className="me-1" />
                      Create RFQ
                    </Button>
                  }
                >
                  <Button
                    variant="primary"
                    onClick={() => navigate('/rfqs/create')}
                  >
                    <FiPlus className="me-1" />
                    Create RFQ
                  </Button>
                </PermissionGate>
              </div>
            </div>
          </Col>
        </Row>

        {/* Summary Cards */}
        {renderSummaryCards()}

        {/* Search and Quick Filters */}
        <Row className="mb-3">
          <Col md={6}>
            <InputGroup>
              <InputGroup.Text>
                <FiSearch />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search RFQs..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={6} className="text-end">
            {Object.values(filters).some(filter => filter !== '') && (
              <Button variant="outline-secondary" onClick={clearFilters}>
                Clear Filters ({Object.values(filters).filter(f => f !== '').length})
              </Button>
            )}
          </Col>
        </Row>

        {/* Main Content */}
        <Card>
          <Card.Header>
            <ul className="nav nav-tabs card-header-tabs">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "all" ? "active" : ""}`}
                  onClick={() => setActiveTab("all")}
                >
                  All RFQs
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "individual" ? "active" : ""}`}
                  onClick={() => setActiveTab("individual")}
                >
                  Individual
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "consolidated" ? "active" : ""}`}
                  onClick={() => setActiveTab("consolidated")}
                >
                  Consolidated
                </button>
              </li>
              {/* <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "pending" ? "active" : ""}`}
                  onClick={() => setActiveTab("pending")}
                >
                  Pending Responses
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === "completed" ? "active" : ""}`}
                  onClick={() => setActiveTab("completed")}
                >
                  Completed
                </button>
              </li> */}
            </ul>
            {/* <span>({summary.total_active || 0})</span> */}
          </Card.Header>
          <Card.Body className="p-0">
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading RFQs...</p>
              </div>
            ) : (
              renderRFQTable()
            )}
          </Card.Body>
        </Card>

        {/* Modals */}
        {renderConsolidationModal()}
        {renderFiltersModal()}
      </Container>
    </Layout>
  );
};

export default RFQManagement;
